import { FilterAdapter, useUrlFilterController } from 'app/components/filters/url-filters';
import { EntityType, getPluginsList } from 'app/console-api/plugins/plugins';
import { useEffect, useState } from 'react';
import { HttpError } from 'app/components/utils/http-error';
import { statusCode } from 'app/constants';
import { SIGN_IN_PATH } from 'app/route/paths';
import Select from 'react-select';
import { AppsFiltersStyles } from 'app/components/common/common-styles';

// Keep PluginDropdownValue shape consistent with your codebase
type PluginDropdownValue = {
  id: number; // numeric ID
  value: string; // string version (react-select)
  label: string;
};

export const TaskTypeAdapter: FilterAdapter<number[]> = {
  key: 'plugins',
  fromUrl: (sp) => {
    const raw = sp.get('plugins');
    if (!raw) return null;
    const nums = raw
      .split(',')
      .map((p) => parseInt(p.trim(), 10))
      .filter((n) => Number.isInteger(n));
    return nums.length > 0 ? nums : null;
  },
  toUrl: (sp, value) => {
    const next = new URLSearchParams(sp.toString());
    if (!value || value.length === 0) {
      next.delete('plugins');
    } else {
      next.set('plugins', value.join(','));
    }
    return next;
  },
  toKey: (v) => `plugins:${v && v.length ? v.join(',') : 'all'}`
};

type TaskTypeFilterProps = {
  eventType?: EntityType;
} & Record<string, any>;

export const TaskTypeFilter = ({ eventType, ...props }: TaskTypeFilterProps) => {
  const ctl = useUrlFilterController();
  const value = ctl.get(TaskTypeAdapter) ?? []; // number[] of selected plugin IDs
  const [pluginList, setPluginList] = useState<PluginDropdownValue[]>([]);

  useEffect(() => {
    getPluginsList(eventType)
      .then((plugins) => {
        const res: PluginDropdownValue[] = (plugins || []).map((plugin: any) => ({
          label: plugin.name,
          value: String(plugin.id), // react-select expects a "value" field
          id: plugin.id
        }));
        setPluginList(res);
      })
      .catch((error: any) => {
        if (error instanceof HttpError && error.statusCode === statusCode.authentication_error) {
          window.location.href = SIGN_IN_PATH;
          return;
        }
        console.error('Failed to fetch plugins:', error);
      });
  }, []);

  // For multi-select, react-select expects an array of option objects as the value
  const selected = pluginList.filter((o) => value.includes(o.id));

  return (
    <Select
      styles={AppsFiltersStyles()}
      {...props}
      isMulti
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
      isSearchable={false}
      options={pluginList}
      value={selected}
      placeholder="All task types"
      onChange={(opts: readonly PluginDropdownValue[] | null) => {
        const ids = (opts ? Array.from(opts) : []).map((o) => o.id);
        ctl.set(TaskTypeAdapter, ids.length ? ids : null);
      }}
    />
  );
};
